import axios, {
  type AxiosProgressEvent,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
} from 'axios';
import log from 'loglevel';

/**
 * Progress handler delegate signature.
 */
export type AxiosProgressHandler = (event: AxiosProgressEvent) => void;

/**
 * Default handling of download progress events.
 * @param event - the axios progress event.
 */
export function progressHandlerDef(event: AxiosProgressEvent) {
  if (event.loaded && event.total) {
    const percentage = Math.round((event.loaded * 100) / event.total);
    log.debug(`Cache Download Progress Event: ${percentage}%`);
  }
}

/**
 * Fetch remote.
 * Note:
 * @param request - Request info.
 * @param onDownloadProgress - Download progress handler.
 */
async function fetchInternal(
  request: RequestInfo,
  onDownloadProgress?: AxiosProgressHandler,
): Promise<Response> {
  const req: Request = new Request(request);

  const config: AxiosRequestConfig = {
    url: req.url,
    method: 'get',
    withCredentials: true,
    headers: req.headers as unknown as AxiosRequestHeaders,
    responseType: 'blob',
    onDownloadProgress,
  };

  const client = axios.create(config);
  const response = await client.get(req.url, config);

  if (response.status !== 200) {
    return Promise.reject(new Error(response.statusText));
  }

  return Promise.resolve(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: Parameterize response type
    new Response(response.data, {
      headers: {
        'content-length': response.headers['Content-Length'] as string,
        'content-type': response.headers['Content-Type'] as string,
      },
      status: response.status,
      statusText: response.statusText,
    }),
  );
}

/**
 * Fetch resource with cache.
 * @param cache - the cache.
 * @param request - the request info.
 * @param onDownloadProgress - the download progress handler.
 */
export function fetch(
  cache: Cache,
  request: RequestInfo,
  onDownloadProgress?: AxiosProgressHandler,
): Promise<Response> {
  return cache.match(request).then(item => {
    // Match found return.
    if (item) {
      return item;
    }
    // Match not found then fetch, save and return.
    return fetchInternal(request, onDownloadProgress).then(response =>
      cache.put(request, response.clone()).then(() => response),
    );
  });
}

/**
 * Fetch multiple resources with cache.
 * @param cache - the cache.
 * @param request - the requests info.
 * @param onDownloadProgress - the download progress handler.
 */
export function fetchAll(
  cache: Cache,
  requests: RequestInfo[],
  onDownloadProgress?: AxiosProgressHandler,
): Promise<Response[]> {
  return Promise.all(requests.map(request => fetch(cache, request, onDownloadProgress)));
}
